import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {MatProgressBar} from '@angular/material/progress-bar';
import {ImageAsset} from '@app/asset';
import {Matrix, Point, Rect} from '@app/shared/euclid';
import {IViewable, ViewerTool} from '@app/viewer/viewable.interface';

import {ViewerToolsService} from '@app/viewer/viewer-tools.service';

import {fromEvent} from 'rxjs';

const kThumbnailSize = 256;

@Component({
    selector: 'frog-image',
    templateUrl: './image.component.html',
    styleUrls: ['./image.component.scss'],
    imports: [MatProgressBar],
})
export class ImageComponent implements OnInit, AfterViewInit, OnChanges, IViewable {
    @Input() asset: ImageAsset;
    @Input() preferences: any;
    @ViewChild('img') img: ElementRef;
    @ViewChild('canvas') canvas: ElementRef;
    @ViewChild('root', { static: true }) root: ElementRef;

    public isScaledFit: boolean;
    public width: number;
    public height: number;
    public debug: any;
    public percent: number;

    private origin: Point;
    private xform: Matrix;
    private main: Matrix;
    private ctx: CanvasRenderingContext2D;
    private element: HTMLImageElement;
    private isMouseDown: boolean;
    private documentElement;

    constructor(
        private viewerToolsService: ViewerToolsService,
        private changeRef: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: any,
    ) {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.origin = new Point();
        this.xform = Matrix.Identity();
        this.main = Matrix.Identity();
    }

    ngOnInit(): void {
        this.documentElement = this.document.documentElement;
        this.emitTools();
    }

    ngOnChanges(changes: SimpleChanges) {
        this.clear();
        this.percent = 0;
        const xhr: XMLHttpRequest = new XMLHttpRequest();
        xhr.open('GET', this.asset.image, true);
        xhr.onprogress = function (e) {
            this.percent = (e.loaded / e.total) * 100;
        }.bind(this);
        xhr.send();
    }

    ngAfterViewInit() {
        this.ctx = this.canvas.nativeElement.getContext('2d');
        this.ctx.imageSmoothingEnabled = this.preferences.antialias;
        this.element = this.img.nativeElement as HTMLImageElement;

        fromEvent(this.element, 'load').subscribe(() => {
            this.scale(this.asset.width, this.asset.height);
            this.scaleToWindow();
        });
    }

    emitTools() {
        const viewerTools = [];
        if (this.isScaledFit) {
            viewerTools.push(
                new ViewerTool({
                    icon: 'zoom_in',
                    tooltip: 'Zoom to original size',
                    action: () => {
                        this.center();
                    },
                }),
            );
        } else {
            viewerTools.push(
                new ViewerTool({
                    icon: 'fit_screen',
                    tooltip: 'Zoom to fit window',
                    action: () => {
                        this.scaleToWindow();
                    },
                }),
            );
        }

        if (this.document.fullscreenElement === null) {
            viewerTools.splice(
                1,
                0,
                new ViewerTool({
                    icon: 'fullscreen',
                    tooltip: 'Fullscreen',
                    action: () => {
                        this.openFullscreen();
                    },
                }),
            );
        }

        this.viewerToolsService.setTools(viewerTools);
    }

    scaleToSource() {
        this.center();
    }

    scaleToWindow() {
        const size = this.xform.rect.fit(
            this.root.nativeElement.clientWidth,
            this.root.nativeElement.clientHeight,
        );
        let scale = size.width / this.asset.width;
        scale = scale > 1.0 ? 1.0 : scale;
        this.center(scale);
        this.isScaledFit = true;
        this.emitTools();
    }

    center(scale = 1.0) {
        this.width = this.root.nativeElement.clientWidth;
        this.height = this.root.nativeElement.clientHeight;
        this.changeRef.detectChanges();

        this.xform = new Matrix([
            [this.asset.width, 0, 0],
            [0, this.asset.height, 0],
            [0, 0, 1],
        ]);
        this.scale(scale, scale);
        const x: number = this.width / 2 - this.xform.elements[0][0] / 2;
        const y: number = this.height / 2 - this.xform.elements[1][1] / 2;
        this.translate(x, y);

        this.main = this.xform;
        this.render();
    }

    // -- Events
    @HostListener('window:mouseup')
    up() {
        this.isMouseDown = false;
        this.main = this.xform;
    }

    // Mouse Events
    mouseDown(event: MouseEvent): void {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();

        if (event.button === 0) {
            this.isMouseDown = true;
            this.origin.x = event.x;
            this.origin.y = event.y;
        }
    }

    mouseMove(event: MouseEvent): void {
        if (this.isMouseDown) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();

            let x = event.clientX - this.origin.x;
            let y = event.clientY - this.origin.y;

            if (event.shiftKey) {
                if (this.asset.width / this.asset.height > 1.0) {
                    y = 0;
                } else {
                    x = 0;
                }
            }

            this.xform = Matrix.Identity().x(this.main);
            this.translate(x, y);
            this.render();
        }
    }

    panStart(event: any): void {}

    // Mobile Events
    panMove(event: any) {
        if (event.pointerType !== 'touch') {
            return;
        }
        event.preventDefault();

        this.xform = Matrix.Identity().x(this.main);
        this.translate(event.deltaX, event.deltaY);
        this.render();
    }

    pinchZoom(event: any): void {
        const scale = event.scale;
        const x = event.center.x;
        const y = event.center.y;
        let xform = this.main.dup();

        // Center
        let translateMatrix = new Matrix([
            [1, 0, 0],
            [0, 1, 0],
            [-x, -y, 1],
        ]);
        xform = xform.x(translateMatrix);

        // Scale
        const scaleMatrix = new Matrix([
            [scale, 0, 0],
            [0, scale, 0],
            [0, 0, 1],
        ]);
        xform = xform.x(scaleMatrix);

        // Un-Center
        translateMatrix = new Matrix([
            [1, 0, 0],
            [0, 1, 0],
            [x, y, 1],
        ]);
        xform = xform.x(translateMatrix);

        this.debug = xform;
        this.xform = xform;
        this.render();
    }

    pinchEnd(event: any): void {
        this.main = this.xform;
    }

    zoom(event: WheelEvent) {
        let scale = 1.0;
        if (event.deltaY < 0) {
            scale += 0.1;
        } else {
            scale -= 0.1;
        }
        const x = event.clientX;
        const y = event.clientY;
        this.xform = this.main.dup();
        this.translate(-x, -y);
        this.scale(scale, scale);
        this.translate(x, y);
        this.main = this.xform;
        this.render();
    }

    clear() {
        if (this.ctx) {
            this.ctx.clearRect(0, 0, this.width - 1, this.height);
        }
    }

    render() {
        this.clear();
        const rect = this.xform.rect;
        this.img.nativeElement.style.left = '';
        this.img.nativeElement.style.top = '';

        this.ctx.drawImage(
            this.element,
            Math.floor(rect.x),
            Math.floor(rect.y),
            Math.floor(rect.width),
            Math.floor(rect.height),
        );

        if (window.innerWidth < 1080) {
            return;
        }

        if (this.preferences.show_minimap) {
            this.renderThumbnail();
        }
    }

    renderThumbnail() {
        const rect = this.xform.rect;
        let minScale, scale;

        const imageWidth = this.element.naturalWidth;
        const imageHeight = this.element.naturalHeight;

        if (imageWidth >= this.asset.height) {
            minScale = kThumbnailSize / imageWidth;
            scale = rect.width / imageWidth;
        } else {
            minScale = kThumbnailSize / imageHeight;
            scale = rect.height / imageHeight;
        }

        const offset = new Point(
            this.width - imageWidth * minScale - 20,
            this.height - imageHeight * minScale - 20,
        );
        const x = Math.abs(Math.min(0, rect.x / scale));
        const y = Math.abs(Math.min(0, rect.y / scale));
        const width =
            imageWidth - Math.abs(Math.max(0, (rect.x + rect.width - this.width) / scale)) - x;
        const height =
            imageHeight - Math.abs(Math.max(0, (rect.y + rect.height - this.height) / scale)) - y;

        const fullRect = new Rect(
            Math.floor(offset.x),
            Math.floor(offset.y),
            Math.floor(imageWidth * minScale),
            Math.floor(imageHeight * minScale),
        );
        const sourceRect = new Rect(x, y, width, height);
        const destRect = new Rect(
            offset.x + x * minScale,
            offset.y + y * minScale,
            width * minScale,
            height * minScale,
        );
        if (fullRect.equals(destRect)) {
            // There's no clipping and the minimap is useless, so just don't show anything
            return;
        }

        this.ctx.globalAlpha = 0.5;
        this.ctx.drawImage(this.element, fullRect.x, fullRect.y, fullRect.width, fullRect.height);
        this.ctx.globalAlpha = 1;
        this.ctx.drawImage(
            this.element,
            sourceRect.x,
            sourceRect.y,
            sourceRect.width,
            sourceRect.height,
            destRect.x,
            destRect.y,
            destRect.width,
            destRect.height,
        );
        this.ctx.strokeStyle = '#fff';
        this.ctx.strokeRect(destRect.x + 0.5, destRect.y + 0.5, destRect.width, destRect.height);
    }

    translate(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [1, 0, 0],
            [0, 1, 0],
            [x, y, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();
    }

    scale(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [x, 0, 0],
            [0, y, 0],
            [0, 0, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();
        this.isScaledFit = false;
        this.emitTools();
    }

    openFullscreen() {
        if (this.documentElement.requestFullscreen) {
            this.documentElement.requestFullscreen();
        } else if (this.documentElement.mozRequestFullScreen) {
            /* Firefox */
            this.documentElement.mozRequestFullScreen();
        } else if (this.documentElement.webkitRequestFullscreen) {
            /* Chrome, Safari and Opera */
            this.documentElement.webkitRequestFullscreen();
        } else if (this.documentElement.msRequestFullscreen) {
            /* IE/Edge */
            this.documentElement.msRequestFullscreen();
        }
    }

    /* Close fullscreen */
    closeFullscreen() {
        if (document.exitFullscreen) {
            this.document.exitFullscreen();
        } else if (this.document.mozCancelFullScreen) {
            /* Firefox */
            this.document.mozCancelFullScreen();
        } else if (this.document.webkitExitFullscreen) {
            /* Chrome, Safari and Opera */
            this.document.webkitExitFullscreen();
        } else if (this.document.msExitFullscreen) {
            /* IE/Edge */
            this.document.msExitFullscreen();
        }
    }
}
